import { keyOfObject } from 'base/helpers';
import { useStableCallback } from 'base/useStableCallback';
import classNames from 'classnames';
import {
  comparer,
  computed,
  IComputedValue,
  makeObservable,
  observable,
} from 'mobx';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { createPortal } from 'react-dom';
import styles from './dnd.css';

type Point = { x: number; y: number };
type DraggableOpts<S, D> = Readonly<{
  source: S | undefined;
  element: React.RefObject<HTMLElement>;
  measureDraggedElement?: MeasureDraggedElement;
  supportsDuplicate: boolean;
  selfDropTarget?: D | undefined;
  onDragStart?: () => void;
  onAfterDrop?: (source: S, destination: D, mode: DropMode) => void;
}>;
type DragData<S, D> = Readonly<{
  draggableId: string;
  pointerId: number;
  pointerCapturedBy: HTMLElement;
  cursorToBox: Point;
  cursorToHovering: Point;
  width: number;
  height: number;
  scale: number;
  source: S;
  supportsDuplicate: boolean;
}>;
type HoverState<S, D> = Readonly<{
  dest: D;
  processDrop: (source: S, dest: D, mode: DropMode) => void;
}>;
export type DropMode = 'default' | 'duplicate';
type DomainRules<S, D> = Readonly<{
  isDropAllowed?: (source: S, dest: D, mode: DropMode) => boolean;
  isSelfDrop: (source: S, dest: D) => boolean;
  cursorOffset?: CursorOffsetCalculator;
  /**
   * If set, the first drop target that is hovered will be "sticky", and will be
   * locked in as the drop target until the drag ends. Useful when draggables
   * can only be dropped in the one target.
   */
  useStickyDropTargets?: boolean;
}>;

class DomainStore<S, D> {
  readonly id: string;

  readonly draggables = observable.map<string, DraggableOpts<S, D>>([], {
    deep: false,
  });
  readonly dropTargets = observable.map<string, DropTargetOpts<S, D>>([], {
    deep: false,
  });

  @observable.ref
  cursorX = 0;
  @observable.ref
  cursorY = 0;
  @observable.ref
  holdingAlt = false;

  @observable.ref
  private dragData: DragData<S, D> | undefined = undefined;

  @observable.ref
  hovering: HoverState<S, D> | undefined = undefined;

  @observable.ref
  stickyDropTargetEl: HTMLElement | undefined = undefined;

  constructor(private readonly rules: DomainRules<S, D>) {
    makeObservable(this);
    this.id = `dnd-domain-${keyOfObject(this)}`;
  }

  dragDataFor(id: string) {
    return this.dragData?.draggableId === id ? this.dragData : undefined;
  }

  startDrag(dragData: DragData<S, D>) {
    this.endDrag();
    this.dragData = dragData;
  }

  endDrag() {
    if (this.dragData) {
      this.dragData.pointerCapturedBy.releasePointerCapture(
        this.dragData.pointerId,
      );
    }
    this.dragData = undefined;
    this.stickyDropTargetEl = undefined;
  }

  drop(dragData: DragData<S, D>) {
    const draggable = this.draggables.get(dragData.draggableId);
    if (!draggable) throw new Error('draggable does not exist in this domain');
    const { source, supportsDuplicate } = dragData;
    this.endDrag();
    if (this.hovering == null) return;
    const { dest, processDrop } = this.hovering;
    this.hovering = undefined;
    const dropMode =
      this.holdingAlt && supportsDuplicate ? 'duplicate' : 'default';
    if (
      (this.rules.isDropAllowed == null ||
        this.rules.isDropAllowed(source, dest, dropMode)) &&
      !(dropMode === 'default' && this.rules.isSelfDrop(source, dest))
    ) {
      processDrop(source, dest, dropMode);
      draggable.onAfterDrop?.(source, dest, dropMode);
    }
  }

  readonly state: DndDomain<S, D>['state'] = computed(
    () => {
      if (this.dragData == null) {
        return;
      }
      const dropMode =
        this.holdingAlt && this.dragData.supportsDuplicate
          ? 'duplicate'
          : 'default';
      const dest =
        this.hovering != null &&
        (this.rules.isDropAllowed == null ||
          this.rules.isDropAllowed(
            this.dragData.source,
            this.hovering.dest,
            dropMode,
          ))
          ? this.hovering.dest
          : undefined;
      return {
        dest,
        drag: this.dragData,
        source: this.dragData.source,
        dropMode,
      };
    },
    { equals: comparer.shallow },
  );
}

/**
 * Implements DnD for "re-ordering" where items can be dragged to different positions
 * in a lists, tree, etc.
 *
 * @type S a representation of "source" of a drag, where something is dragged from.
 * @type D a representation of "destination" of a drag, where something is dragged to.
 */
export function createDndDomain<S, D>(
  rules: DomainRules<S, D>,
): DndDomain<S, D> {
  const domainStore = new DomainStore(rules);

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && domainStore.state.get() != null) {
      domainStore.endDrag();
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
    }
    domainStore.holdingAlt =
      e.altKey || e.key === 'Alt' || e.key === 'AltGraph';
  });
  document.addEventListener('keyup', (e) => {
    domainStore.holdingAlt =
      e.altKey && !(e.key === 'Alt' || e.key === 'AltGraph');
  });
  window.addEventListener('blur', () => {
    domainStore.endDrag();
  });

  const Draggable = observer(function _Draggable({
    children,
    source,
    dragPreviewClass,
    dragPreviewHoveringClass,
    imposterClass,
    selfDropTarget,
    supportsDuplicate = false,
    onDragStart,
    measureDraggedElement = defaultMeasureDraggedElement,
    onAfterDrop,
    dropTarget,
    className,
    renderDragPreview,
    noShadow = false,
  }: DraggableProps<S, D>) {
    const ref = React.useRef<HTMLDivElement>(null);
    const id = React.useId();
    const dragData = domainStore.dragDataFor(id);

    React.useEffect(
      function unsetDragOnUnmount() {
        return () => {
          if (domainStore.dragDataFor(id)) domainStore.endDrag();
        };
      },
      [id],
    );

    React.useEffect(() => {
      domainStore.draggables.set(id, {
        source,
        element: ref,
        measureDraggedElement,
        selfDropTarget,
        supportsDuplicate,
        onDragStart,
        onAfterDrop,
      });
      return () => {
        domainStore.draggables.delete(id);
      };
    }, [
      id,
      source,
      ref,
      measureDraggedElement,
      selfDropTarget,
      supportsDuplicate,
      onDragStart,
      onAfterDrop,
    ]);

    let inlineContent = dragData ? (
      domainStore.holdingAlt && supportsDuplicate ? (
        children
      ) : selfDropTarget == null ? (
        <div
          className={classNames(styles.imposter, imposterClass)}
          style={{ width: dragData.width, height: dragData.height }}
        />
      ) : null
    ) : (
      children
    );

    return (
      <div
        ref={ref}
        draggable={useFirefoxDraggableFix(ref)}
        onDragStart={preventNativeDrag}
        className={className}
        {...useDropTargetAttrs(dropTarget, domainStore)}
        {...useDragSource({ draggableId: id, domainStore, rules })}
        style={{ cursor: dragData ? 'grabbing' : undefined }}
      >
        {dragData &&
          createPortal(
            <div
              className={classNames(
                styles.dragPreview,
                !noShadow && styles.shadow,
                domainStore.hovering != null && dragPreviewHoveringClass,
                dragPreviewClass,
              )}
              style={{
                width: dragData.width,
                height: dragData.height,
                transform: `translate(${
                  domainStore.cursorX - dragData.cursorToBox.x
                }px, ${domainStore.cursorY - dragData.cursorToBox.y}px) scale(${
                  dragData.scale
                })`,
              }}
              {...{ [IS_DRAG_PREVIEW_ATTR]: true }}
            >
              {renderDragPreview != null
                ? renderDragPreview({ children })
                : children}
            </div>,
            document.body,
          )}
        {inlineContent}
      </div>
    );
  });

  function DropTarget({ opts, ...props }: DropTargetProps<S, D>) {
    return <div {...props} {...useDropTargetAttrs(opts, domainStore)} />;
  }

  function DragHandle({ draggableId, children, onClick }: DragHandleProps) {
    return (
      <div
        className={styles.dragHandle}
        {...useDragSource({
          draggableId,
          domainStore,
          rules,
          dragStartDistancePx: 0,
          onClick,
        })}
      >
        {children}
      </div>
    );
  }

  function getDraggableId(
    el: HTMLElement,
    position: 'parent' | 'child',
  ): string | undefined {
    const query = `[${DATA_DRAG_SOURCE_ATTR}]`;
    const closest =
      position === 'parent' ? el.closest(query) : el.querySelector(query);
    return closest?.getAttribute(DATA_DRAG_SOURCE_ATTR) || undefined;
  }

  return {
    state: domainStore.state,
    getCursorPosition: () =>
      domainStore.state.get() != null
        ? {
            x: domainStore.cursorX,
            y: domainStore.cursorY,
          }
        : undefined,
    Draggable,
    DropTarget,
    DragHandle,
    getDraggableId,
  };
}

function useDropTargetAttrs<S, D>(
  opts: MaybeDropTargetOpts<S, D>,
  domainStore: DomainStore<S, D>,
) {
  const id = React.useId();
  React.useEffect(() => {
    if (!opts) return;
    domainStore.dropTargets.set(id, opts);
    return () => {
      domainStore.dropTargets.delete(id);
    };
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, opts]);
  return opts
    ? {
        [DATA_DOMAIN_ATTR]: domainStore.id,
        [DATA_DROP_TARGET_ATTR]: id,
      }
    : {};
}

function useDragSource<S, D>({
  draggableId,
  domainStore,
  rules,
  dragStartDistancePx = 10,
  onClick,
}: {
  draggableId: string;
  domainStore: DomainStore<S, D>;
  rules: DomainRules<S, D>;
  dragStartDistancePx?: number;
  onClick?: () => void;
}) {
  const startDrag = useStableCallback(
    (pointerId: number, startPos: Point, currentPos: Point) => {
      const draggable = domainStore.draggables.get(draggableId);
      if (draggable == null) return;
      const {
        source,
        element: { current: el },
        measureDraggedElement = defaultMeasureDraggedElement,
        selfDropTarget,
        supportsDuplicate,
        onDragStart,
      } = draggable;
      if (el == null || source == null) return;

      delete startPoints.current[pointerId];
      el.setPointerCapture(pointerId);
      domainStore.cursorX = currentPos.x;
      domainStore.cursorY = currentPos.y;
      const box = el.getBoundingClientRect();

      domainStore.startDrag({
        draggableId,
        ...(rules.cursorOffset || maintainCursorPosition)(
          currentPos,
          startPos,
          box,
        ),
        pointerId,
        pointerCapturedBy: el,
        ...measureDraggedElement(el),
        scale: box.width / el.offsetWidth,
        supportsDuplicate,
        source,
      });
      if (selfDropTarget) {
        domainStore.hovering = {
          dest: selfDropTarget,
          processDrop: () => {
            throw new Error('self drop should not be processed');
          },
        };
      }
      onDragStart?.();

      // Event listener is added to window so that the event always fires even
      // if the React re-renders and invalidates the callback.
      window.addEventListener('pointerup', onPointerUpGlobal);
    },
  );

  /** To start a drag, the user needs to pointerDown within the draggable element and then move their pointer a bit. */
  const startPoints = React.useRef<Record<number, Point>>({});

  const onPointerMove = useStableCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.pointerType !== 'mouse') return;
      const dragData = domainStore.dragDataFor(draggableId);

      if (!dragData) {
        // Try to start the drag
        const start = startPoints.current[e.pointerId];
        if (!e.isPrimary || e.buttons !== 1 || !start) return;
        const dist = Math.sqrt(
          Math.pow(e.clientX - start.x, 2) + Math.pow(e.clientY - start.y, 2),
        );
        if (dist >= dragStartDistancePx) {
          startDrag(e.pointerId, start, { x: e.clientX, y: e.clientY });
        }
        return;
      }

      if (dragData.pointerId !== e.pointerId) return;
      // Handle a drag move event
      domainStore.cursorX = e.clientX;
      domainStore.cursorY = e.clientY;
      const { hovering: oldHovering } = domainStore;
      const newHovering = getHoveringAtPoint(
        e.clientX + dragData.cursorToHovering.x,
        e.clientY + dragData.cursorToHovering.y,
        dragData.source,
        domainStore,
      );
      if (newHovering) {
        domainStore.hovering = newHovering.state;
        if (rules.useStickyDropTargets && !domainStore.stickyDropTargetEl) {
          domainStore.stickyDropTargetEl = newHovering.dropTargetEl;
        }
      }
      if (oldHovering && !newHovering) {
        // avoid flicker when moving between elements, by unsetting
        // after a timeout
        setTimeout(() => {
          if (domainStore.hovering === oldHovering) {
            domainStore.hovering = undefined;
          }
        }, 100);
      }
    },
  );

  const onPointerDown = useStableCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (
        !e.isPrimary ||
        e.buttons !== 1 ||
        e.pointerType !== 'mouse' ||
        isDragBlocked(e.target, e.currentTarget)
      )
        return;
      if (
        e.target instanceof Element &&
        e.target.closest(`[${DATA_DRAG_SOURCE_ATTR}]`) !== e.currentTarget
      ) {
        return;
      }
      startPoints.current[e.pointerId] = {
        x: e.clientX,
        y: e.clientY,
      };
      if (!onClick) onPointerMove(e);
    },
  );

  const onPointerUpGlobal = useStableCallback((e: PointerEvent) => {
    window.removeEventListener('pointerup', onPointerUpGlobal);

    delete startPoints.current[e.pointerId];
    const dragData = domainStore.dragDataFor(draggableId);
    if (dragData && dragData.pointerId === e.pointerId) {
      domainStore.drop(dragData);
    }
  });

  const onPointerUp = useStableCallback(
    (e: React.PointerEvent<HTMLDivElement>) => onPointerUpGlobal(e.nativeEvent),
  );

  return {
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onClick,
    [DATA_DRAG_SOURCE_ATTR]: draggableId,
    [DATA_DOMAIN_ATTR]: domainStore.id,
  };
}

/**
 * If the user is using a drag handle, their cursor is outside the draggable element.
 *
 * If we used their cursor position for `getHoveringAtPoint`, they wouldn't be able to drop
 * initially.  So we calculate an offset that pretends their cursor is within the
 * draggable element initially.
 **/
function offsetToFitWithin(cursorX: number, left: number, right: number) {
  const width = right - left;
  // Add padding, so if the user's mouse jitters a bit it's still within the box
  const padding = Math.min(width * 0.2, 64);
  if (cursorX < left) return left - cursorX + padding;
  if (cursorX > right) return right - cursorX - padding;
  return 0;
}

function getHoveringAtPoint<S, D>(
  x: number,
  y: number,
  source: S,
  domainStore: DomainStore<S, D>,
): { state: HoverState<S, D>; dropTargetEl: HTMLElement } | undefined {
  const elements = document.elementsFromPoint(x, y);
  const dropTargetEl =
    domainStore.stickyDropTargetEl ??
    elements.find(
      (el): el is HTMLElement =>
        el instanceof HTMLElement &&
        el.getAttribute(DATA_DOMAIN_ATTR) === domainStore.id &&
        el.hasAttribute(DATA_DROP_TARGET_ATTR),
    );
  if (!dropTargetEl) return;
  const dropTarget = domainStore.dropTargets.get(
    dropTargetEl.getAttribute(DATA_DROP_TARGET_ATTR) || '',
  );
  if (!dropTarget) return;
  const dest = dropTarget.mouseToDestination(x, y, dropTargetEl, source);
  if (dest == null) return;
  return {
    state: { dest, processDrop: dropTarget.processDrop },
    dropTargetEl,
  };
}

function preventNativeDrag(e: React.DragEvent) {
  e.preventDefault();
  e.stopPropagation();
}

/**
 * On firefox if a <textarea> is contained within an element with "draggable=true",
 * it is not possible to change the text of a textarea.
 *
 * This listens for the focused element, and if it is a textarea inside the current
 * draggbale it disables "draggable=true".
 **/
function useFirefoxDraggableFix(el: React.RefObject<HTMLElement>) {
  if (!window.navigator.userAgent.includes('Firefox/')) return true;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [draggable, setDraggable] = React.useState(true);
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line react-hooks/rules-of-hooks
  React.useEffect(() => {
    const listener = () => {
      const focused = document.activeElement;
      const isTextarea = focused?.nodeName === 'TEXTAREA';
      setDraggable(!isTextarea || !el.current?.contains(focused));
    };
    document.addEventListener('focusin', listener);
    return () => document.removeEventListener('focusin', listener);
  }, [el]);
  return draggable;
}

/** Trying to select text in a textarea shouldn't cause a parent of the text area to drag */
function isDragBlocked(el: EventTarget, draggable: HTMLElement): boolean {
  if (!(el instanceof HTMLElement)) return false;
  const textarea = el.closest('input, textarea, [contenteditable="true"]');
  return textarea != null && draggable.contains(textarea);
}

export type MeasureDraggedElement = (el: HTMLElement) => {
  width: number;
  height: number;
};
export const defaultMeasureDraggedElement: MeasureDraggedElement = (el) => ({
  width: el.offsetWidth,
  height: el.offsetHeight,
});

const DATA_DOMAIN_ATTR = 'data-rldnddomain';
const DATA_DROP_TARGET_ATTR = 'data-rldroptarget';
const DATA_DRAG_SOURCE_ATTR = 'data-rldragsource';
export const IS_DRAG_PREVIEW_ATTR = 'data-rldragpreview';

type DropTargetOpts<S, D> = {
  mouseToDestination: (
    clientX: number,
    clientY: number,
    dropTarget: HTMLElement,
    source: S,
  ) => D | undefined;
  processDrop: (source: S, dest: D, mode: DropMode) => void;
};

type MaybeDropTargetOpts<S, D> = DropTargetOpts<S, D> | undefined;

type DraggableProps<S, D> = {
  children: React.ReactNode;
  className?: string;
  /** If source is undefined, the element is not draggable */
  source: S | undefined;
  dragPreviewClass?: string;
  dragPreviewHoveringClass?: string;
  onDragStart?: () => void;
  measureDraggedElement?: MeasureDraggedElement;
  supportsDuplicate?: boolean;
  onAfterDrop?: (source: S, dest: D, mode: DropMode) => void;
  dropTarget?: MaybeDropTargetOpts<S, D>;
  renderDragPreview?: ({
    children,
  }: {
    children: React.ReactNode;
  }) => React.ReactNode;
  noShadow?: boolean;
} & (
  | {
      /**
       * If "selfDropTarget" is provided, the element will be removed from the flow
       * when dragging starts.  We'll say the drag starts hovering "selfDropTarget".
       *
       * This means when the user moves the mouse away, the original element's
       * location will disappear.
       **/
      selfDropTarget: D | undefined;
      imposterClass?: undefined;
    }
  | {
      selfDropTarget?: undefined;
      /**
       * A class to use for the imposter element.  The imposter is the element which
       * remains in the orginal location when the element is being dragged.
       *
       * Can not be used with "selfDropTarget".
       **/
      imposterClass?: string;
    }
);

type DropTargetProps<S, D> = {
  opts: MaybeDropTargetOpts<S, D>;
} & Pick<React.HTMLProps<HTMLDivElement>, 'className' | 'style' | 'children'>;

type DragHandleProps = {
  draggableId: string;
  children: React.ReactNode;
  onClick?: () => void;
};

export type DndDomain<S, D> = {
  /** The state of the current drag, if any is happening */
  state: IComputedValue<
    | {
        drag: { width: number; height: number };
        source: S;
        /* Undefined if no destination is being hovered */
        dest: D | undefined;
        dropMode: DropMode;
      }
    | undefined
  >;
  /** Returns the drag cursor's position, if the user is dragging */
  getCursorPosition: () => Point | undefined;
  DropTarget: React.ComponentType<DropTargetProps<S, D>>;
  Draggable: React.ComponentType<DraggableProps<S, D>>;
  DragHandle: React.ComponentType<DragHandleProps>;
  getDraggableId: (
    el: HTMLElement,
    direction: 'parent' | 'child',
  ) => string | undefined;
};

export const BLOCK_DRAG_START = {
  // this attribute means it is treated as a "draggable child" by the parent
  [DATA_DRAG_SOURCE_ATTR]: 'not-draggable',
};

type CursorOffsetCalculator = (
  currentCursor: Point,
  startCursor: Point,
  draggable: DOMRect,
) => { cursorToHovering: Point; cursorToBox: Point };

/**
 * The cursor offsets will ensure the draggable doesn't move when the drag is started.
 * The cursor offsets are non-zero in this mode.
 */
const maintainCursorPosition: CursorOffsetCalculator = (
  currentCursor: Point,
  startCursor: Point,
  draggable: DOMRect,
) => {
  const cursorToHovering = {
    x: offsetToFitWithin(currentCursor.x, draggable.left, draggable.right),
    y: offsetToFitWithin(currentCursor.y, draggable.top, draggable.bottom),
  };
  const cursorToBox = {
    x: startCursor.x - draggable.left,
    y: startCursor.y - draggable.top,
  };
  return { cursorToHovering, cursorToBox };
};
